// The Ownezzy User Guide (ownezzy.com/guide): parts, guides and their steps. Every guide has its own address
// (/guide/<slug>) and every step its own link (/guide/<slug>#<step id>), so a single step can be shared.
// A step's points are text, or { text, sub: [points] } for sub-points (and sub-sub-points). In text, **bold** and
// [label](url) links work. shots: true shows the step's desktop and mobile screenshots
// (public/guide-img/<slug>/<step id>-desktop.webp and -mobile.webp, annotated with numbered red boxes; a point that
// starts with a number in a circle, ①, matches the box with that number); shots: 'desktop' for a step that only
// exists on a computer.

export const PARTS = [
  { id: 'start', title: 'Getting started', icon: '🚀', text: 'Find your way around ownezzy.com and pick a ready-made website for your shop, boutique or home business.' },
  { id: 'get', title: 'Get your website', icon: '🌐', text: 'Get a website for your shop free: start a free trial or buy a plan, get your login details and sign in for the first time.' },
  { id: 'admin', title: 'Use the admin panel', icon: '🛠️', text: 'Edit your website and add your products without coding, from the admin panel on your phone or computer.' },
  { id: 'run', title: 'Run your store', icon: '📦', text: 'Get orders on WhatsApp, take UPI and Cash on Delivery payments, set up shipping, run offers on Instagram and grow.' },
];

export const GUIDES = [
  {
    slug: 'open-ownezzy',
    part: 'start',
    title: 'Open Ownezzy and find your way around',
    summary: 'What you see on ownezzy.com, from the top menu to the plans.',
    steps: [
      {
        id: 'open-the-website',
        title: 'Open ownezzy.com',
        shots: true,
        points: [
          'Open any browser (Chrome, Safari, Edge or Firefox) on your phone or computer.',
          'Type **ownezzy.com** in the address bar and press Enter, or open [ownezzy.com](https://ownezzy.com).',
          {
            text: 'The home page opens. At the top you see:',
            sub: [
              '① The Ownezzy logo: tap it on any page to come back to the home page.',
              '② The menu: Home, All Websites, Plan, Become a partner and Contact Us.',
              '③ The search button: search all 100+ websites by business type.',
            ],
          },
          'On a phone the menu is folded into the ☰ button at the top right.',
        ],
      },
      {
        id: 'hero-designs',
        title: 'Look through the featured store designs',
        shots: true,
        points: [
          'The big banner below the menu shows one ready-made store design at a time.',
          {
            text: 'On each design:',
            sub: [
              '① **Get Website**: start your own website in this design (see [Start a free trial](/guide/start-free-trial)).',
              '② **Use this design**: the same, from the design picture.',
              '③ **View live demo**: open the real demo store to look around as a customer.',
              '④ The arrows (‹ ›): see the previous or next design. The designs also change by themselves.',
            ],
          },
          'The last slide, **And more**, takes you down to every design.',
        ],
      },
      {
        id: 'choose-by-business',
        title: 'Choose a design by your business',
        shots: true,
        points: [
          'Scroll down to **Search Website as per Your Business**.',
          {
            text: 'You can:',
            sub: [
              '① Type your business in the search box (for example "cakes", "sarees" or "candles") and press **Search**.',
              '② Tap one of the business buttons (Clothing, Plants, Gifts, Footwear…) to see designs for it.',
              '③ Tap **Create Your Own Custom Website** if your business is not in the list.',
            ],
          },
        ],
      },
      {
        id: 'see-the-plans',
        title: 'See the plans and prices',
        shots: true,
        points: [
          'Tap **Plan** in the menu, or open [ownezzy.com/#plans](https://ownezzy.com/#plans).',
          {
            text: 'Each plan card shows:',
            sub: [
              '① The price and how long it lasts (free trial, monthly or yearly).',
              '② What is included: storage, emails, your own domain on paid plans and more.',
            ],
          },
          'Outside India, an approximate US$ price is shown under the rupee price. Plans are charged in rupees.',
          'Read [Start a free trial](/guide/start-free-trial) or [Buy a plan](/guide/buy-a-plan) to get started.',
        ],
      },
    ],
  },
  {
    slug: 'browse-websites',
    part: 'start',
    title: 'Go to All Websites',
    summary: 'Open the list of every ready-made website (ownezzy.com/websites).',
    steps: [
      {
        id: 'open-all-websites',
        title: 'Open the All Websites page',
        shots: true,
        points: [
          {
            text: 'Choose one way:',
            sub: [
              '① Tap **All Websites** in the top menu (on a phone, tap ☰ first).',
              'Or open [ownezzy.com/websites](https://ownezzy.com/websites) directly.',
              'Or tap **Explore all 100+ ready-to-use business websites** on the home page.',
            ],
          },
        ],
      },
      {
        id: 'what-is-on-the-page',
        title: 'What is on the page',
        shots: true,
        points: [
          '① The search box: search by business, product or store name.',
          '② The category buttons: Clothing & Fashion, Plants & Garden, Gifts & Favors and more.',
          '③ How many websites match, and **Create Custom Website** for any other business.',
          '④ Sort: Recommended, or by name (A to Z or Z to A).',
          {
            text: '⑤ The website cards. Each card shows:',
            sub: [
              'A picture of the store and its web address.',
              'The kind of business and the demo store\'s name.',
              '**View Demo** to open the store, and **Start Free Trial** to get it (see [Start a free trial](/guide/start-free-trial)).',
            ],
          },
          'Visitors from outside India see the international stores (USA, Canada, UK, Europe, UAE, Saudi Arabia, Japan and Australia) at the top. In India they come after the Indian designs.',
        ],
      },
    ],
  },
  {
    slug: 'search-websites',
    part: 'start',
    title: 'Search for the right website',
    summary: 'Find a design for your business by searching, filtering and sorting.',
    steps: [
      {
        id: 'search-by-name',
        title: 'Search by business or product',
        shots: true,
        points: [
          'Open [ownezzy.com/websites](https://ownezzy.com/websites).',
          '① Type what you sell in the search box, for example **candles**, **cakes**, **sarees** or **plants**.',
          '② The list changes as you type and shows how many websites match.',
          {
            text: 'Tips:',
            sub: [
              'Use one simple word ("bags", not "ladies handbags shop").',
              'Try the product too ("jhumka", "biryani", "oud").',
              'Clear the box to see every website again.',
            ],
          },
        ],
      },
      {
        id: 'filter-by-category',
        title: 'Filter by category',
        shots: true,
        points: [
          '① Tap a category button, for example **Gifts & Favors**. Only those websites show.',
          '② Use the arrows (‹ ›) to see more categories.',
          'Tap **All Websites** to remove the filter.',
          'Search and category work together: choose a category and then type in the search box.',
        ],
      },
      {
        id: 'sort-the-list',
        title: 'Sort the list',
        shots: 'desktop',
        points: [
          '① Open the **Sort by** list (on a computer; on a phone the list keeps the recommended order).',
          {
            text: 'Choose:',
            sub: ['**Recommended / Most Popular**: the usual order.', '**Name (A to Z)** or **Name (Z to A)**: alphabetical.'],
          },
        ],
      },
      {
        id: 'quick-search',
        title: 'Search from any page',
        shots: true,
        points: [
          '① Tap **Search 100+ websites** in the top menu (on a computer you can also press ⌘K or Ctrl+K).',
          '② Type your business. Matching websites show as you type.',
          'Tap a result to open its demo store.',
        ],
      },
      {
        id: 'not-in-the-list',
        title: 'Your business is not in the list',
        shots: true,
        points: [
          '① Tap **Create Custom Website**.',
          {
            text: 'Fill in:',
            sub: ['Your business type (type anything, for example "Handmade leather wallets").', 'Your store name, WhatsApp number and email.'],
          },
          'Your store is made with a design that suits your business. The steps are the same as [Start a free trial](/guide/start-free-trial).',
        ],
      },
    ],
  },
  {
    slug: 'websites-by-business',
    part: 'start',
    title: 'Set up a website for your kind of business',
    summary: 'Ready-made websites and setup tips for clothing, jewellery, crafts, gifts, food, beauty, footwear, phone cases, pets and plants.',
    steps: [
      {
        id: 'clothing-and-boutiques',
        title: 'Clothing, sarees and boutiques',
        points: [
          'See the [clothing website](/create/clothing-website) and the [kids ethnic wear website](/create/kids-ethnic-wear-website), or search "boutique", "saree" or "kurti" on [ownezzy.com/websites](https://ownezzy.com/websites).',
          {
            text: 'Works for:',
            sub: [
              'Sarees: cotton, silk, Banarasi and handloom sarees, sold direct to customers.',
              'Kurtis and suits: chikankari kurtis, stitched suits, dress material, dupattas and stoles.',
              'Western wear, T-shirt brands, activewear and gym clothing, modest wear and abayas.',
              'Kids ethnic wear like pattu pavadai, bridal wear and second hand or sustainable fashion.',
            ],
          },
          {
            text: 'Set it up:',
            sub: [
              'Add sizes (S, M, L, XL or 2-3 years), each with its own price if needed (see [Sizes and a price for each size](/guide/products#sizes-and-prices)).',
              'Size chart: add it as one of the 3 product photos, or write the measurements in the description.',
              'Custom tailoring: add each stitching service as a product; after the order, the customer sends measurements on WhatsApp.',
              'Wholesale: use **Sold as** (per piece, per set) and share your catalog link with shops on WhatsApp.',
            ],
          },
        ],
      },
      {
        id: 'jewellery-and-accessories',
        title: 'Jewellery and accessories',
        points: [
          'See the [jewellery website](/create/jewelry-website), or search "jewellery" on [ownezzy.com/websites](https://ownezzy.com/websites).',
          {
            text: 'Works for:',
            sub: [
              'Artificial, imitation and fashion jewellery, oxidised, kundan (kundankari), temple and bridal imitation sets.',
              'Silver, gold plated, brass and copper jewellery.',
              'Handmade jewellery: beaded, terracotta, resin and customised name jewellery.',
              'Fashion earrings and hair accessories.',
            ],
          },
          {
            text: 'Set it up:',
            sub: [
              'Up to 3 close-up photos per product make a photo gallery on the product page.',
              'Use **Colours** for finishes (gold, silver, rose gold) and **Size based price** for ring or bangle sizes.',
              'Turn on UPI so customers pay instantly at checkout (see [Store settings](/guide/store-settings#upi-payments-razorpay)).',
              'Customised name jewellery: ask for the name in the description, and confirm it on WhatsApp after the order.',
            ],
          },
        ],
      },
      {
        id: 'crafts-and-home-decor',
        title: 'Handicrafts, art and home decor',
        points: [
          'See the [handicrafts website](/create/crochet-handicrafts-website) and the [home decor website](/create/home-decor-website).',
          {
            text: 'Works for:',
            sub: [
              'Resin art: clocks, bookmarks, trays and keepsakes.',
              'Paintings and Madhubani art.',
              'Crochet, macrame wall hangings, jute bags, handmade stationery and journals.',
              'Scented candles, ceramic pottery, Jaipur blue pottery, wooden handicrafts, brass artifacts, clay diyas and festive decor.',
            ],
          },
          {
            text: 'Set it up:',
            sub: [
              'Your own store is an alternative to an artisan marketplace: no commission, and your customers stay yours.',
              'Use sizes for clock or painting sizes (12 inch, 18 inch), each with its own price.',
              'Before Diwali and other festivals, put festive decor in [Featured and offers](/guide/featured-and-offers) and post a banner (see [Make an ad banner](/guide/coupons-and-banners#make-a-banner)).',
            ],
          },
        ],
      },
      {
        id: 'gifts-and-hampers',
        title: 'Gifts and hampers',
        points: [
          'See the [gifts and hampers website](/create/gifts-hampers-website).',
          'Works for gift hampers, dry fruit festive hampers, custom gift baskets and personalised photo frame gifts.',
          {
            text: 'Set it up:',
            sub: [
              'Add each hamper as a product, with its sizes (Small, Medium, Large) and prices.',
              'Personalised gifts like photo frames: in **⚙️ More options**, tick **Shopper adds a name or message** and choose how many **Photos the shopper uploads**. Customers add them when they order.',
              'Create a festive coupon code (see [Create a discount coupon](/guide/coupons-and-banners#create-a-coupon)).',
            ],
          },
        ],
      },
      {
        id: 'food-and-grocery',
        title: 'Food, bakery and grocery',
        points: [
          'See the [grocery website](/create/grocery-website) and the [bakery and cakes website](/create/bakery-cakes-website).',
          {
            text: 'Works for:',
            sub: [
              'Local kirana and grocery stores, fresh vegetable delivery, dairy and milk delivery.',
              'Cake shops, home bakers, homemade chocolates and mithai sweet shops.',
              'Homemade food, cloud kitchens and tiffin services.',
              'Pickles and papad, namkeen and snacks, organic spices, honey, cold pressed oil, dry fruits and tea.',
            ],
          },
          {
            text: 'Set it up:',
            sub: [
              'Use **Sold as** (per kg, per litre) and sizes by weight (250 g, 500 g, 1 kg), each with its own price.',
              'Set **What shoppers pick is called** to Weight.',
              'Cakes: sizes by weight (0.5 kg, 1 kg); the customer sends the message for the cake on WhatsApp.',
              'Local delivery: set your shipping charge and free delivery amount in [Store settings](/guide/store-settings), and offer Cash on Delivery.',
            ],
          },
        ],
      },
      {
        id: 'beauty-and-wellness',
        title: 'Beauty, skincare and ayurvedic products',
        points: [
          'See the [beauty and cosmetics website](/create/beauty-cosmetics-website).',
          'Works for skincare brands, organic soap, handmade cosmetics and ayurvedic herbal products.',
          'Write the ingredients and how to use it in the description; use sizes for pack sizes (50 ml, 100 ml).',
        ],
      },
      {
        id: 'footwear-cases-pets-plants',
        title: 'Footwear, phone cases, pets and plants',
        points: [
          {
            text: 'Footwear: see the [footwear website](/create/footwear-website).',
            sub: ['For leather shoe stores, handcrafted juttis and Kolhapuri chappals.', 'Add shoe sizes (6, 7, 8, 9) and colours to each product.'],
          },
          {
            text: 'Phone cases: see the [phone cases website](/create/phone-cases-website).',
            sub: ['For custom phone cases and printed mobile covers.', 'Add the phone models as sizes; set **What shoppers pick is called** to Model.'],
          },
          'Pets: see the [pet supplies website](/create/pet-supplies-website), for pet food and supplies.',
          'Plants: search "plants" on [ownezzy.com/websites](https://ownezzy.com/websites) for an online plant nursery.',
        ],
      },
    ],
  },
  {
    slug: 'view-store-demo',
    part: 'start',
    title: 'Look around a demo store',
    summary: 'Open a live demo store and try it as a customer, before you choose it.',
    steps: [
      {
        id: 'open-a-demo',
        title: 'Open a demo store',
        shots: true,
        points: [
          'On [ownezzy.com/websites](https://ownezzy.com/websites), find a website you like.',
          '① Tap **View Demo** on its card (or tap the picture).',
          'The demo store opens in a new tab at its own address, for example [fashion.ownezzy.com](https://fashion.ownezzy.com).',
        ],
      },
      {
        id: 'demo-home-page',
        title: 'The demo store\'s home page',
        shots: true,
        points: [
          'A demo is a real, working store with sample products. Your store looks the same, with your products.',
          {
            text: 'Look at:',
            sub: [
              '① The categories in the menu and as round pictures.',
              '② The banner with offers, and its buttons.',
              '③ The bar at the top: **Get it for free** starts your own store in this design.',
            ],
          },
          'Scroll down to see featured products, offers, the "Shop by" section and the trust points.',
        ],
      },
      {
        id: 'demo-product-page',
        title: 'Open a product',
        shots: true,
        points: [
          'Tap any product to open its page.',
          {
            text: 'A product page shows:',
            sub: [
              '① Photos: swipe or tap to see them all.',
              '② The name, price and the original price crossed out when there is a discount.',
              '③ Sizes and colours to pick, when the product has them.',
              '④ **Add to cart** and **Buy now**.',
            ],
          },
          'You can add products to the cart and go to checkout to see how ordering works. Demo orders are not real.',
        ],
      },
      {
        id: 'get-this-website',
        title: 'Get this website',
        shots: true,
        points: [
          '① Tap **Get it for free** at the top of the demo store.',
          'The sign-up opens with this design already chosen. Follow [Start a free trial](/guide/start-free-trial).',
          '② Want to compare? Tap **Explore 100+ live templates** to go back to every website.',
        ],
      },
    ],
  },
  {
    slug: 'start-free-trial',
    part: 'get',
    title: 'Start a free trial',
    summary: 'Get your own store free for 7 days: store name, WhatsApp and email, a code, and you are live.',
    steps: [
      {
        id: 'open-the-sign-up',
        title: 'Open the sign-up',
        shots: true,
        points: [
          {
            text: 'Start from any of these:',
            sub: [
              '**Start Free Trial** on a website card on [ownezzy.com/websites](https://ownezzy.com/websites).',
              '**Get it for free** at the top of a demo store.',
              '**Get Website** on the home page banner.',
            ],
          },
          '① The sign-up opens with the design you picked. **View demo** opens that store again.',
          '② The box for your store name. Your store\'s free web address appears as you type, for example **kavya-boutique.ownezzy.com**.',
        ],
      },
      {
        id: 'name-your-store',
        title: 'Name your store',
        shots: true,
        points: [
          '① Type the name customers will see, for example **Kavya Boutique**.',
          {
            text: '② The address is checked straight away:',
            sub: ['✓ green: the address is free and will be yours.', 'Red: someone has it. Change the name a little, for example add your city.'],
          },
          '③ Tap **Continue**.',
        ],
      },
      {
        id: 'whatsapp-and-email',
        title: 'Add your WhatsApp number and email',
        shots: true,
        points: [
          '① Your 10-digit WhatsApp number. Your login details and new-order alerts come here.',
          '② Your email address. A 6-digit code is sent here to check it is yours.',
          '③ Tap **Email me a code →**.',
          'Use an email you can open now. Gmail works well.',
        ],
      },
      {
        id: 'enter-the-code',
        title: 'Enter the code from your email',
        shots: true,
        points: [
          'Open your email and find **"… is your Ownezzy verification code"** (see [The emails you receive](/guide/login-email#verification-code-email)).',
          '① Type the 6-digit code.',
          '② Tap **Verify & continue →**.',
          {
            text: 'No code?',
            sub: ['Look in Spam and Promotions.', '③ Wait 30 seconds and tap **Resend code**.', 'Check that the email address is right; **Change number or email** lets you fix it.'],
          },
        ],
      },
      {
        id: 'choose-free-trial',
        title: 'Choose the free trial and create your store',
        shots: true,
        points: [
          '① **7-Day Free Trial** is selected for you. No card or payment is needed.',
          '② Your store\'s address: **yourstore.ownezzy.com**, free.',
          '③ Tap **Start my free website**.',
          'Want your own domain like yourstore.com? Choose Monthly or Yearly instead: see [Buy a plan](/guide/buy-a-plan).',
        ],
      },
      {
        id: 'store-ready',
        title: 'Your store is ready',
        shots: true,
        points: [
          {
            text: '① Your login details show on the screen:',
            sub: ['**Store website**: your shop\'s address for customers.', '**Admin panel**: where you manage your store.', '**Admin ID**: your WhatsApp number.', '**Password**: shown only once.'],
          },
          '② Tap **Copy** next to each one, or copy all, and keep them safe.',
          '③ The same details are emailed to you and sent on WhatsApp (see [The emails you receive](/guide/login-email#website-ready-email)).',
          '④ Tap **Open my admin panel** to sign in (see [First login and password](/guide/first-login-change-password)).',
          'The free trial lasts 7 days. Buy a plan before it ends to keep your store open.',
        ],
      },
    ],
  },
  {
    slug: 'buy-a-plan',
    part: 'get',
    title: 'Buy a plan',
    summary: 'Pick Monthly or Yearly, choose a free address or your own domain, add a coupon and pay.',
    steps: [
      {
        id: 'choose-a-plan',
        title: 'Choose Monthly or Yearly',
        shots: true,
        points: [
          'Follow [Start a free trial](/guide/start-free-trial) up to **Choose how to start**.',
          {
            text: '① Tap a plan:',
            sub: ['**Monthly**: pay each month. New customers may see a first-month offer.', '**Yearly**: pay once a year and save money.'],
          },
          'The price shows on the right. Outside India the approximate US$ price shows under it.',
        ],
      },
      {
        id: 'choose-your-address',
        title: 'Choose your website address',
        shots: true,
        points: [
          {
            text: '① Choose:',
            sub: [
              '**Free subdomain**: yourstore.ownezzy.com, at no cost.',
              '**Main domain**: your own address like yourstore.com (yearly fee for the domain).',
            ],
          },
          {
            text: 'For a main domain:',
            sub: [
              '② Type a name. Available domains show with their first-year price and renewal price.',
              '③ Tap one to choose it.',
              '**I will buy the domain myself**: you buy it from any seller and we link it for you. Your store opens on the free subdomain until then.',
            ],
          },
        ],
      },
      {
        id: 'coupon-and-total',
        title: 'Add a coupon and check the total',
        shots: true,
        points: [
          '① Have a coupon? Type it and tap **Apply** (coupons work on plans without a first-month offer).',
          {
            text: '② Check the summary:',
            sub: ['The plan, and any coupon discount.', 'GST, when the plan has it.', 'The domain and its GST, for a main domain.', '**Pay now**: the total you pay.'],
          },
          '③ Tap **Pay ₹… →**.',
        ],
      },
      {
        id: 'pay',
        title: 'Pay',
        points: [
          'A secure Razorpay payment window opens.',
          {
            text: 'Pay with:',
            sub: ['**UPI**: scan the QR code or pick your UPI app (Google Pay, PhonePe, Paytm…).', '**Card**: debit or credit card, Indian or international.', '**Net banking** or **wallet**.'],
          },
          'Do not close the page until it says the payment is done.',
          'Closed it by mistake? Tap **Pay ₹… now** again; you are never charged twice for the same purchase.',
          'Prices are in Indian rupees. International cards are charged in rupees and your bank converts the amount.',
        ],
      },
      {
        id: 'after-payment',
        title: 'After paying',
        points: [
          'Your store is created and your login details show, the same as the free trial (see [Your store is ready](/guide/start-free-trial#store-ready)).',
          '"Payment received" shows with your plan.',
          'A main domain starts working within 24 hours. Use your free ownezzy.com address until then.',
          'Problem with a payment? See our [Cancellation & Refund Policy](/refund-policy) or [contact us](/contact).',
        ],
      },
    ],
  },
  {
    slug: 'login-email',
    part: 'get',
    title: 'The emails you receive',
    summary: 'The verification code email and the "Your website is ready" email with your login details.',
    steps: [
      {
        id: 'verification-code-email',
        title: 'The verification code email',
        shots: true,
        points: [
          'Sent when you tap **Email me a code** during sign-up.',
          '① Subject: **"123456 is your Ownezzy verification code"**.',
          '② The 6-digit code. Type it in the sign-up within 10 minutes.',
          'Never share this code with anyone. Ownezzy never asks for it on a call.',
        ],
      },
      {
        id: 'website-ready-email',
        title: 'The "Your Ownezzy website is ready" email',
        shots: true,
        points: [
          'Sent as soon as your store is created. The same details come on WhatsApp.',
          {
            text: '① Your details:',
            sub: ['**Store website**: share this with customers.', '**Admin panel**: admin.ownezzy.com.', '**Admin ID**: your WhatsApp number.', '**Admin password**: your first password.'],
          },
          '② **Open your website** and **Open your admin panel** buttons.',
          'Keep this email safe. Change the password after your first login (see [Change your password](/guide/first-login-change-password#change-password-later)).',
        ],
      },
      {
        id: 'cannot-find-the-email',
        title: 'Cannot find the email?',
        points: [
          'Search your inbox for **Ownezzy**.',
          'Look in **Spam**, **Junk** and **Promotions**, and mark it "Not spam" so the next ones arrive in your inbox.',
          'Check the WhatsApp message: it has the same details.',
          'Still nothing? You can sign in with a code sent to your email (see [Sign in with an email code](/guide/first-login-change-password#sign-in-with-a-code)), or [contact us](/contact).',
        ],
      },
    ],
  },
  {
    slug: 'first-login-change-password',
    part: 'get',
    title: 'First login and your password',
    summary: 'Sign in to the admin panel, set your own password, sign in with an email code, and reset a forgotten password.',
    steps: [
      {
        id: 'open-the-admin-panel',
        title: 'Sign in to the admin panel',
        shots: true,
        points: [
          'Open [admin.ownezzy.com](https://admin.ownezzy.com), or tap **Open your admin panel** in the email.',
          '① Keep **Password** selected.',
          '② Type your **Admin ID** (your WhatsApp number), email or username.',
          '③ Type the password from the email.',
          '④ Tap **Sign in**.',
        ],
      },
      {
        id: 'choose-a-new-password',
        title: 'Choose your own password',
        shots: true,
        points: [
          'The first time you sign in, you are asked to choose your own password.',
          '① Type the password from the email (current password).',
          '② Type a new password: at least 8 letters, with a number. Do not use your name or phone number.',
          '③ Type the new password again (**Confirm new password**).',
          '④ Tap **Change password**. You go to your dashboard.',
        ],
      },
      {
        id: 'sign-in-with-a-code',
        title: 'Sign in with an email code (no password)',
        shots: true,
        points: [
          '① Tap **Email OTP** on the sign-in page.',
          '② Type your email address.',
          '③ Tap **Email me a code**.',
          'Type the 6-digit code from your email and tap **Verify & sign in**.',
        ],
      },
      {
        id: 'forgot-password',
        title: 'Forgot your password',
        shots: true,
        points: [
          '① Tap **Forgot password?** on the sign-in page.',
          '② Type your email, mobile number or username.',
          '③ Tap **Send reset code**.',
          'Type the reset code from your email and your new password, then tap **Set new password**.',
        ],
      },
      {
        id: 'change-password-later',
        title: 'Change your password any time',
        shots: true,
        points: [
          'In the admin panel, tap your initial (the round button at the top right), or open [admin.ownezzy.com/admin/profile](https://admin.ownezzy.com/admin/profile).',
          '① In the **Password** section, type your current password, the new password, and the new password again.',
          '② Tap **Change password**.',
          'Change it straight away if you think someone else knows it.',
        ],
      },
    ],
  },
  {
    slug: 'admin-panel-tour',
    part: 'admin',
    title: 'Find your way around the admin panel',
    summary: 'The menu, the dashboard and its setup checklist, your websites and the top bar.',
    steps: [
      {
        id: 'the-menu',
        title: 'The menu',
        shots: true,
        points: [
          'Sign in at [admin.ownezzy.com](https://admin.ownezzy.com) (see [First login](/guide/first-login-change-password)).',
          {
            text: '① On a computer the menu is on the left:',
            sub: [
              '**Overview**: your dashboard and setup checklist.',
              '**Order management**: every order (the number shows new ones).',
              '**My websites**: your stores, to edit them or see their orders.',
              '**Store settings**: business details, shipping, GST and payments.',
              '**SEO**, **Staff**, **Analytics**, **Data migration**, **Profile**, **Marketing**, **Billing** and **Suggestions**.',
            ],
          },
          '① On a phone the main items are in the bar at the bottom; **More** opens the rest.',
        ],
      },
      {
        id: 'overview-and-setup',
        title: 'The dashboard and its setup checklist',
        shots: true,
        points: [
          'Open [Overview](https://admin.ownezzy.com/admin/overview).',
          {
            text: '① **Finish setting up your store** lists what is left to do, for example:',
            sub: ['Connect Razorpay for UPI payments.', 'Confirm shipping charges and GST.', 'Complete your business information.'],
          },
          '② Tap the button on a step to go straight to it. Done steps show ✓ **Completed**.',
          'Below it you see your sales, orders and visitors at a glance.',
        ],
      },
      {
        id: 'my-websites',
        title: 'Your websites',
        shots: true,
        points: [
          'Open [My websites](https://admin.ownezzy.com/admin/my-websites).',
          {
            text: '① Each website shows:',
            sub: ['Its name and web address (tap the address to open your store).', 'The plan, start date and expiry date with the days left.', 'Pending orders, and whether it is active.'],
          },
          '② **Edit site**: change the website and its products (see [Open the website editor](/guide/website-editor)).',
          '③ **Manage orders**: this website\'s orders.',
        ],
      },
      {
        id: 'the-top-bar',
        title: 'The top bar',
        shots: true,
        points: [
          '① 🎓 Play the welcome tour again.',
          '② 🔔 Alerts: new orders, returns and messages.',
          '③ **Sign out**.',
          '④ Your initial: opens your [Profile](https://admin.ownezzy.com/admin/profile) (name, email and password).',
        ],
      },
    ],
  },
  {
    slug: 'website-editor',
    part: 'admin',
    title: 'Open the website editor',
    summary: 'Open a website for editing, find the sections, see a preview and save.',
    steps: [
      {
        id: 'open-the-editor',
        title: 'Open the editor',
        shots: true,
        points: [
          'In [My websites](https://admin.ownezzy.com/admin/my-websites), tap ① **Edit site** next to your website.',
          'The website editor opens. Its address ends in **/edit**, so you can bookmark it.',
        ],
      },
      {
        id: 'editor-sections',
        title: 'The editor\'s sections',
        shots: true,
        points: [
          {
            text: '① The sections, in groups:',
            sub: [
              '**Home page**: Content (banners and cards), Featured, Offers.',
              '**Catalogue**: Categories, Products.',
              '**Pages**: About us, Blog, Policies.',
              '**Look & settings**: Design, SEO, Shipping & tax, Store settings.',
            ],
          },
          '② The fields of the section you opened.',
          'On a phone the sections scroll sideways at the top; use ‹ › to move.',
        ],
      },
      {
        id: 'preview-and-save',
        title: 'Preview and save your changes',
        shots: true,
        points: [
          {
            text: '① Choose how to work (on a computer):',
            sub: ['**Editor full**: only the fields.', '**Side by side**: the fields and your live store next to them.', '**Preview full**: only your store.'],
          },
          '② **Preview** opens your store to check it.',
          '③ **Save changes** puts your edits on your store. It shows **✓ Saved** when everything is saved.',
          'Products save by themselves when you tap **Save product**; other sections need **Save changes**.',
          'Leaving with unsaved changes? The editor asks you first, so nothing is lost by mistake.',
        ],
      },
    ],
  },
  {
    slug: 'home-page-content',
    part: 'admin',
    title: 'Change your home page',
    summary: 'The announcement bar, banners, trust points, highlight cards and the Shop by section.',
    steps: [
      {
        id: 'announcement-bar',
        title: 'The announcement bar',
        shots: true,
        points: [
          'In the editor, open **Content**.',
          '① Type a short message for the top of your store, for example "Free delivery above ₹999 • COD available".',
          'Separate several messages with •. Leave it empty to hide the bar.',
          'Tap **Save changes**.',
        ],
      },
      {
        id: 'hero-banners',
        title: 'Banners',
        shots: true,
        points: [
          'Up to 4 banners rotate at the top of your home page.',
          {
            text: '① For each banner:',
            sub: [
              '**Image with text** (title, description and buttons on the picture) or **Image only** (a picture that already has your message).',
              '**Banner title** and **Banner description**.',
              {
                text: 'The picture:',
                sub: ['Paste an image link, or tap **Or upload banner image**.', 'Best size: wide, about 1600 × 700 px.', 'Optional **Mobile image**: a tall picture (about 900 × 1400 px) shown on phones.'],
              },
              '**Remove** deletes the banner.',
            ],
          },
          '② **＋ Add banner** adds another one (up to 4).',
        ],
      },
      {
        id: 'trust-points-and-cards',
        title: 'Trust points and highlight cards',
        shots: true,
        points: [
          '① **Trust points**: the 4 short points under the banner, like "Free delivery" or "Easy returns".',
          {
            text: 'For each point:',
            sub: ['An emoji icon, a title and one short line.', 'The switch hides one point; the switch at the top hides all of them.'],
          },
          '② **Highlight cards**: the 3 coloured cards below the trust points, edited the same way.',
        ],
      },
      {
        id: 'shop-by-section',
        title: 'The Shop by section',
        shots: true,
        points: [
          '① The section with category buttons and "under ₹…" price tiles.',
          {
            text: 'You can change:',
            sub: [
              '**Show this section** on or off.',
              'The small heading, title and description.',
              '**Category chips**: pick an emoji and a category for each button (empty shows all your categories).',
              '**Price tiles**: the text above the amount and up to 4 amounts.',
            ],
          },
          'Empty fields keep the defaults. Tap **Save changes**.',
        ],
      },
    ],
  },
  {
    slug: 'categories',
    part: 'admin',
    title: 'Manage categories',
    summary: 'Add, rename, hide and remove the categories your products are grouped in.',
    steps: [
      {
        id: 'category-list',
        title: 'Add, rename, hide or remove a category',
        shots: true,
        points: [
          'In the editor, open **Categories**.',
          '① Type in a category\'s box to rename it. Its products move with it.',
          '② The number of products in it.',
          {
            text: '③ **Active / Inactive**:',
            sub: ['Active: shoppers see the category and its products.', 'Inactive: hidden from shoppers, kept in your admin panel.'],
          },
          '④ **Remove** deletes the category (move or delete its products first).',
          '⑤ **＋ Add category** adds a new one.',
          'Tap **Save changes** at the top.',
        ],
      },
    ],
  },
  {
    slug: 'products',
    part: 'admin',
    title: 'Add and edit products',
    summary: 'Add products with photos, prices, sizes and colours, edit or delete them, and import many at once.',
    steps: [
      {
        id: 'products-list',
        title: 'Your product list',
        shots: true,
        points: [
          'In the editor, open **Products**.',
          '① Search by name, category or tag.',
          '② **＋ Add product**.',
          '③ **Import / export**: download your products as Excel or CSV, or import a file to add many at once.',
          '④ Products are grouped by category. Each row shows the photo, name, price and stock, with **✏️ Edit** and 🗑️ delete.',
        ],
      },
      {
        id: 'add-a-product',
        title: 'Add a product',
        shots: true,
        points: [
          'Tap **＋ Add product**. The form opens (full screen on a phone).',
          '① **Photos**: tap **Add photos** to choose up to 3. The first is the main photo; tap **Make main** on another to change it. Square photos look best.',
          '② **Name & category**: the product name and its category.',
          '③ **Price & stock**: the original price, the discounted (selling) price, the stock quantity and the **Available to buy** switch.',
          '④ **Description** (optional): what it is, the size and material, what is included.',
          '⑤ Tap **＋ Add product**. It is saved and live on your store straight away.',
        ],
      },
      {
        id: 'sizes-and-prices',
        title: 'Sizes and a price for each size',
        shots: true,
        points: [
          {
            text: '① In **Price & stock**, choose:',
            sub: [
              '**Fixed price**: one price. You can still add sizes sold at that price (S, M, L or 10 cm, 20 cm).',
              '**Size based price**: a different price for each size.',
            ],
          },
          {
            text: '② With **Size based price**, each row has:',
            sub: ['The size (for example L, 100 inch or 500 g).', 'Its original price and its discounted price.', '**＋ Add more** adds another size; × removes one.'],
          },
          '③ **What shoppers pick is called**: Size, Length, Weight… (shown on your store).',
          'Shoppers pick a size before adding to the cart and pay that size\'s price.',
        ],
      },
      {
        id: 'colours-and-more',
        title: 'Colours and more options',
        shots: true,
        points: [
          {
            text: '① **Colours** (optional):',
            sub: ['Tap a colour to add it, or pick any colour and name it.', '**Detect colours from photo** suggests the colours in your main photo.', 'Shoppers choose a colour before adding to the cart.'],
          },
          {
            text: '② **More options**:',
            sub: ['**Sold as**: per piece, per kg, per litre…', '**Tags**: words shown on the product page and used by search, separated by commas.'],
          },
        ],
      },
      {
        id: 'edit-or-delete',
        title: 'Edit or delete a product',
        shots: true,
        points: [
          '① Tap a product (or **✏️ Edit**). The same form opens with its details.',
          '② Change anything and tap **✓ Save product**. Your store updates straight away.',
          '③ **🗑️ Delete** removes the product from your store after you confirm.',
        ],
      },
    ],
  },
  {
    slug: 'featured-and-offers',
    part: 'admin',
    title: 'Featured products and offers',
    summary: 'Choose the products shown first on your home page and the ones on offer.',
    steps: [
      {
        id: 'featured-products',
        title: 'Featured products',
        shots: true,
        points: [
          'In the editor, open **Featured**.',
          '① The products shown in a grid near the top of your home page, in this order.',
          '② Use ↑ ↓ to change the order, and **Remove** to take one out.',
          '③ **Add a product**: pick one from the list to feature it (up to 12).',
          'Tap **Save changes**.',
        ],
      },
      {
        id: 'offers',
        title: 'Offers',
        shots: true,
        points: [
          'In the editor, open **Offers**.',
          '① Name the section (for example "Diwali Offers"). It shows as its own row and in the menu.',
          '② **Put a product on offer**: pick it from the list. It is added to **On offer now**.',
          '③ For each offer product, set the original price and the offer price; the discount % shows automatically. You can add a short offer title and note.',
          'Tap **Save changes**.',
        ],
      },
    ],
  },
  {
    slug: 'manage-orders',
    part: 'run',
    title: 'Manage your orders',
    summary: 'See every order, update its status, open its details, edit it, ship it and handle returns.',
    steps: [
      {
        id: 'open-orders',
        title: 'Open your orders',
        shots: true,
        points: [
          {
            text: 'Open orders in one of these ways:',
            sub: ['**Order management** in the menu (the number shows orders waiting).', '**Manage orders** next to a website in [My websites](https://admin.ownezzy.com/admin/my-websites).', 'The 🔔 alert or WhatsApp message for a new order.'],
          },
          '① The totals at the top: all orders, those that need processing, in transit, delivered and the order value.',
          '② **Excel** / **CSV**: download your orders.',
          '③ The list: each order with the customer, date, items, total, payment and status.',
        ],
      },
      {
        id: 'find-an-order',
        title: 'Find an order',
        shots: true,
        points: [
          {
            text: '① Narrow the list:',
            sub: ['Search by order number, customer name or phone.', '**Status**: Pending, Confirmed, Packed, Shipped, Delivered…', '**Payment**: COD, UPI, card…', '**From / To**: a date range.', 'Sort newest or oldest first.'],
          },
          '**Reset filters** shows every order again.',
        ],
      },
      {
        id: 'update-the-status',
        title: 'Update an order\'s status',
        shots: true,
        points: [
          '① Pick the new status in the order\'s **Status** box.',
          {
            text: 'The usual order of statuses:',
            sub: ['**Pending**: just placed.', '**Confirmed**: you accepted it.', '**Processing** and **Packed**: getting it ready.', '**Shipped** and **Out for delivery**: on the way.', '**Delivered**: done.', '**Cancelled** or **Refunded** if needed.'],
          },
          'The customer sees every change on the store and gets an update, so keep it current.',
        ],
      },
      {
        id: 'order-details',
        title: 'See and edit an order',
        shots: true,
        points: [
          '① **View**: the full order with the products, sizes and colours chosen, the customer\'s address and phone, the payment and the status history.',
          '② **Edit**: change the delivery details, status, payment method or payment status.',
          '③ **🚚 Ship**: book a courier through Shiprocket, once it is connected in [Store settings](/guide/store-settings#courier).',
          'Paid by manual UPI? Check the money arrived, then tap **✓ Mark as paid** in the order.',
        ],
      },
      {
        id: 'returns-and-refunds',
        title: 'Returns and refunds',
        shots: true,
        points: [
          '① **↩ Returns & refunds**: the return and replacement requests from your customers.',
          '② **Return policy**: turn returns on or off and set how many days customers have.',
          'Open a request to approve or reject it, and record the refund or replacement.',
        ],
      },
    ],
  },
  {
    slug: 'store-settings',
    part: 'run',
    title: 'Store settings: business, shipping and payments',
    summary: 'Your business details, shipping charges and GST, UPI payments, manual UPI and the courier.',
    steps: [
      {
        id: 'business-information',
        title: 'Business information',
        shots: true,
        points: [
          'Open [Store settings](https://admin.ownezzy.com/admin/store-settings). If you have more than one website, pick the website first.',
          '① Your full name, store name, email, contact number and business address. Customers see these on your store and orders.',
          '② Tap **✓ Save business information**.',
        ],
      },
      {
        id: 'shipping-and-gst',
        title: 'Shipping charges and GST',
        shots: true,
        points: [
          {
            text: '① Fill in:',
            sub: ['**Shipping charge (₹)**: added to every order.', '**Free shipping above (₹)**: orders above this pay no shipping (0 = never free).', '**GST (%)**: added at checkout (0 = no GST).'],
          },
          '② Tap **✓ Save shipping & tax**.',
        ],
      },
      {
        id: 'upi-payments-razorpay',
        title: 'Online payments (Razorpay)',
        shots: true,
        points: [
          'Customers pay by UPI (PhonePe, Google Pay, any UPI app or QR) straight into your own account.',
          {
            text: '① Get your keys from Razorpay:',
            sub: ['Sign up or sign in at razorpay.com and complete your KYC.', 'Go to **Account & Settings → API Keys** and generate a key.', 'Copy the **Key ID** and **Key secret**.'],
          },
          '② Turn **Status** to **On (Razorpay)**, paste the Key ID and Key secret.',
          '③ Tap **✓ Save UPI payments (Razorpay)**.',
          'Never share your key secret with anyone else.',
        ],
      },
      {
        id: 'manual-upi',
        title: 'Manual UPI (no gateway)',
        shots: true,
        points: [
          'Customers pay to your UPI ID and send the order to you on WhatsApp. You mark it paid after checking.',
          '① Your UPI ID, the name shown in the UPI app and your WhatsApp number for orders.',
          '② Tap **✓ Save manual UPI payments**.',
        ],
      },
      {
        id: 'courier',
        title: 'Courier (Shiprocket)',
        shots: true,
        points: [
          'Book Delhivery, Bluedart, Ekart and more from your orders.',
          {
            text: '① Fill in:',
            sub: ['Your Shiprocket API user\'s email and password (create an API user in Shiprocket: Settings → API).', 'The pickup address name and pincode, as in Shiprocket.', 'Your usual parcel weight and size.'],
          },
          '② Tap **✓ Save courier (Shiprocket)**. **🚚 Ship** then works on your orders.',
        ],
      },
    ],
  },
  {
    slug: 'coupons-and-banners',
    part: 'run',
    title: 'Discount coupons and ad banners',
    summary: 'Create coupon codes for your customers and make ready-to-post banners for Instagram and WhatsApp.',
    steps: [
      {
        id: 'create-a-coupon',
        title: 'Create a discount coupon',
        shots: true,
        points: [
          'Open [Marketing](https://admin.ownezzy.com/admin/marketing) and tap **🏷️ Discount Coupons**, then **＋ New coupon**.',
          {
            text: '① Fill in:',
            sub: [
              '**Code**: what customers type, for example DIWALI10.',
              '**Discount type**: **Percent (%)** or **Fixed amount (₹)**, and how much.',
              {
                text: 'Optional limits:',
                sub: ['**Most it takes off** (for percent coupons).', '**Order at least (₹)**.', '**Total uses**, **Last day** and **Once per customer**.'],
              },
              'A **note** shown with the coupon.',
            ],
          },
          '② Keep **Active** on and tap **Save coupon**.',
          'Share the code on Instagram and WhatsApp. Customers enter it at checkout.',
        ],
      },
      {
        id: 'make-a-banner',
        title: 'Make an ad banner',
        shots: true,
        points: [
          'In [Marketing](https://admin.ownezzy.com/admin/marketing), tap **🎨 Banner Ads Maker**.',
          '① Choose the size: Instagram/Facebook post, Instagram story or WhatsApp status, or Facebook cover.',
          '② Type the badge, headline, text and button, and add a picture of your product.',
          '③ See the preview, then **⬇ Download PNG**, **Share banner** or **🔗 Copy banner link**.',
        ],
      },
    ],
  },
  {
    slug: 'billing-and-domain',
    part: 'run',
    title: 'Renew your plan and link your domain',
    summary: 'See when your plan ends, pay to renew, see past payments and connect your own domain.',
    steps: [
      {
        id: 'renew-your-plan',
        title: 'Renew your plan',
        shots: true,
        points: [
          'Open [Billing](https://admin.ownezzy.com/admin/billing) (or [My websites](https://admin.ownezzy.com/admin/my-websites)).',
          '① Each website shows its plan, start date, expiry date and the days left.',
          '② **Pay now** shows when 5 days or fewer are left (or after it expires). Choose Monthly or Yearly and pay by UPI or card.',
          'Pay before the expiry date so your store stays open.',
          '③ **Payment history**: every payment with its date, amount and method.',
        ],
      },
      {
        id: 'link-your-domain',
        title: 'Link your own domain',
        points: [
          'Chose a main domain when you bought your plan? We register it and link it to your store within 24 hours. Nothing to do.',
          {
            text: 'Bought a domain yourself (for example from GoDaddy or Namecheap)?',
            sub: ['[Contact us](/contact) on WhatsApp or email with your store address and your domain.', 'We tell you the settings to add at your domain seller, then link it for you.'],
          },
          'Your store keeps working on its ownezzy.com address meanwhile, so share that until the domain is ready.',
        ],
      },
    ],
  },
  {
    slug: 'staff',
    part: 'run',
    title: 'Add staff',
    summary: 'Let family or employees help with orders or products, each with their own login.',
    steps: [
      {
        id: 'add-staff',
        title: 'Add a staff member',
        shots: true,
        points: [
          'Open [Staff](https://admin.ownezzy.com/admin/staff) and tap **＋ Add staff**.',
          '① Their full name, email and mobile number.',
          {
            text: '② **What can they do?** Tick only what they need:',
            sub: ['**Edit website**: products, categories and the home page.', '**Manage orders**.', '**Shoppers & reviews**.', '**Store settings**.'],
          },
          'They get their own login details. Remove them any time from the same page.',
        ],
      },
    ],
  },
  {
    slug: 'seo',
    part: 'run',
    title: 'Get found on Google (SEO)',
    summary: 'Set the title, description and keywords Google shows for your store.',
    steps: [
      {
        id: 'edit-search-details',
        title: 'Edit how your store shows on Google',
        shots: true,
        points: [
          'Open [SEO](https://admin.ownezzy.com/admin/seo).',
          '① **Google Search Result Preview**: how your store looks in Google.',
          {
            text: '② Edit:',
            sub: ['**Store Page Title**: your store name and what you sell (about 60 letters). **Use Recommended Title** fills one in.', '**Meta Description**: one or two sentences about your store (about 155 letters).', '**Target Search Keywords**: words people search for; tap a suggestion to add it.'],
          },
          '③ Tap **💾 Save SEO Settings**.',
          'Also share your store link everywhere and add products with clear names and descriptions: that helps the most.',
        ],
      },
    ],
  },
];

export const guideBySlug = (slug) => GUIDES.find((g) => g.slug === slug);
